import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Chip,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Divider,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PageBreadcrumbs from '../../components/common/PageBreadcrumbs';
import GlobalMaterialTable from '../../components/common/GlobalMaterialTable';
import { getFeedbackBySlotKey } from '../../services/promptConfigurationService';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  AssignmentOutlined,
  ListAltOutlined,
  VisibilityOutlined,
  CalendarTodayOutlined,
  ContentCopyOutlined,
  InfoOutlined,
} from '@mui/icons-material';

const formatDateTime = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(date);
};

const normalizeFeedbackRule = (feedback) => ({
  ...feedback,
  id: feedback.resolved_rule_id || feedback.id || '',
  slot_key: feedback.slot_key || '',
  rule_text: feedback.comment || '-',
  status: feedback.status || '-',
  source_feedback_id: feedback.id || '',
  created_by: feedback.username || '-',
  created_at_raw: feedback.created_at || null,
  updated_at_raw: feedback.updated_at || null,
  created_at: formatDateTime(feedback.created_at),
  request_id: feedback.request_id || '-',
  reaction: feedback.reaction || '-',
});
const normalizeFeedback = (feedback) => ({
  ...feedback,
  id: feedback.id || '',
  request_id: feedback.request_id || '-',
  username: feedback.username || '-',
  status: feedback.status || '-',
  reaction: feedback.reaction || '-',
  comment: feedback.comment || '',
  created_at: formatDateTime(feedback.created_at),
  updated_at: formatDateTime(feedback.updated_at),
});

const PromptRules = () => {
  const { slotKey } = useParams();
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedRuleId, setSelectedRuleId] = useState('');
  const [selectedRule, setSelectedRule] = useState(null);
  const [selectedFeedbacks, setSelectedFeedbacks] = useState([]);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);

  const workflow = slotKey?.split('.')[0] || 'address_change';
  const step = slotKey?.split('.')[1] || 'extraction';

  useEffect(() => {
    let isMounted = true;

    const loadFeedbackRules = async () => {
      setIsLoading(true);
      setErrorMessage('');
      setSelectedRule(null);
      setSelectedRuleId('');
      setSelectedFeedbacks([]);

      try {
        const slotFeedbacks = await getFeedbackBySlotKey(slotKey);
        if (!isMounted) return;

        const feedbackRows = slotFeedbacks.map((feedback) => normalizeFeedbackRule(feedback));
        setRules(feedbackRows);
        setSelectedFeedbacks(slotFeedbacks.map((feedback) => normalizeFeedback(feedback)));
      } catch (error) {
        if (!isMounted) return;
        setErrorMessage(
          error?.response?.data?.message || error?.message || 'Unable to load prompt rule feedback.'
        );
        setRules([]);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadFeedbackRules();

    return () => {
      isMounted = false;
    };
  }, [slotKey]);

  const handleRuleRowClick = async (rule) => {
    const selectedSlotKey = rule?.slot_key || slotKey;
    if (!selectedSlotKey) return;

    setSelectedRuleId(rule?.id || '');
    setSelectedRule(rule || null);
    setSelectedFeedbacks([]);
    setErrorMessage('');
    setIsFeedbackLoading(true);

    try {
      const slotFeedbacks = await getFeedbackBySlotKey(selectedSlotKey);
      setSelectedFeedbacks(slotFeedbacks.map((feedback) => normalizeFeedback(feedback)));
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message || error?.message || 'Unable to load selected prompt rule feedback.'
      );
    } finally {
      setIsFeedbackLoading(false);
    }
  };

  const displayedFeedbacks = selectedFeedbacks.length ? selectedFeedbacks : rules;
  const openFeedbacks = displayedFeedbacks.filter((feedback) => feedback.status === 'open');
  const resolvedFeedbacks = displayedFeedbacks.filter((feedback) => feedback.status && feedback.status !== 'open');
  const latestFeedback = displayedFeedbacks[0] || null;
  const lastFeedbackBy = latestFeedback?.username || latestFeedback?.created_by || '-';
  const lastFeedbackAt = latestFeedback?.updated_at || latestFeedback?.created_at || '-';
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const columns = useMemo(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
        size: 60,
      },
      {
        header: 'Feedback Comment',
        accessorKey: 'rule_text',
        Cell: ({ row }) => (
          <Typography variant="body2" sx={{ color: '#334155' }}>
            {row.original.rule_text || '-'}
          </Typography>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        size: 90,
        Cell: ({ row }) => (
          <Chip
            size="small"
            label={row.original.status || '-'}
            sx={{
              height: 22,
              bgcolor: row.original.status === 'open' ? '#EFF6FF' : '#F8FAFC',
              color: row.original.status === 'open' ? '#1d4ed8' : '#64748b',
              fontWeight: 600,
              textTransform: 'capitalize',
            }}
          />
        ),
      },
      {
        header: 'Source Feedback',
        accessorKey: 'source_feedback_id',
        size: 140,
        Cell: ({ row }) => (
          row.original.source_feedback_id ? (
            <Link
              component="button"
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/feedback/${row.original.source_feedback_id}`);
              }}
              sx={{ textDecoration: 'none', color: '#1d8ef0', fontWeight: 500, fontSize: '0.875rem' }}
            >
              {row.original.source_feedback_id}
            </Link>
          ) : '-'
        ),
      },
      {
        header: 'Created',
        accessorKey: 'created_at',
        size: 120,
        Cell: ({ row }) => (
          <Stack spacing={0}>
            <Typography variant="body2" sx={{ color: '#334155' }}>
              {row.original.created_at}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              by {row.original.created_by}
            </Typography>
          </Stack>
        ),
      },
      {
        header: 'Actions',
        id: 'actions',
        size: 80,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            color="primary"
            onClick={(event) => {
              event.stopPropagation();
              handleRuleRowClick(row.original);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [navigate, rules]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 100px)' }}>
      <Box sx={{ flex: 1, mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Box sx={{ mb: 1 }}>
            <PageBreadcrumbs
              items={[
                { label: 'Prompt Rules', path: '/prompt-rules' },
                { label: `${workflow}.${step}`, path: null },
              ]}
              backPath="/prompt-rules"
            />
          </Box>
          {errorMessage && (
            <Alert severity="error" sx={{ mx: 1, mb: 1 }}>
              {errorMessage}
            </Alert>
          )}
          <Box
            sx={{
              display: 'flex',
              mx: 1,
              mt: 2,
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Prompt Rules
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                Review feedback captured for this prompt slot and use it to guide prompt rule updates.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="outlined"
                sx={{
                  height: 40,
                  textTransform: 'none',
                  borderColor: '#e2e8f0',
                  color: '#334155',
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Preview Effective Prompt
              </Button>

              <IconButton
                sx={{
                  height: 40,
                  width: 40,
                  border: '1px solid #e2e8f0',
                  borderRadius: 2,
                }}
              >
                <RefreshIcon sx={{ color: '#64748b', fontSize: 20 }} />
              </IconButton>
            </Stack>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
          {/* Info Bar / Header Card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 2,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {/* Big Icon */}
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                bgcolor: '#f0f9ff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <AssignmentOutlined sx={{ fontSize: 27, color: '#1d8ef0' }} />
            </Box>

            {/* Info Columns */}
            <Box
              sx={{
                display: 'flex',
                flexGrow: 1,
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box sx={{ px: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                  Step Key
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {slotKey || '-'}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

              <Box sx={{ px: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                  Workflow
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  {workflow}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

              <Box sx={{ px: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                  Base Prompt
                </Typography>
                <Link
                  component="button"
                  sx={{ textDecoration: 'none', color: '#1d8ef0', fontWeight: 600 }}
                >
                  -
                </Link>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

              <Box sx={{ px: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                  Open Feedback
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#16a34a' }}>
                  {openFeedbacks.length || '-'}
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

              <Box sx={{ px: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                  Version
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                  -
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0' }} />

              <Box sx={{ px: 2 }}>
                <Typography variant="caption" sx={{ color: '#64748b', display: 'block', mb: 0.5 }}>
                  Last Updated
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CalendarTodayOutlined sx={{ fontSize: 16, color: '#64748b' }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                    {lastFeedbackAt}
                  </Typography>
                </Stack>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ borderColor: '#e2e8f0', mx: 2 }} />
              <Box>
                <Button
                  variant="outlined"
                  sx={{
                    textTransform: 'none',
                    borderColor: '#e2e8f0',
                    color: '#1e88e5',
                    fontWeight: 600,
                    minWidth: '130px',
                  }}
                >
                  Base Prompt
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: '#e2e8f0' }}>
            <Tabs
              value={0}
              textColor="primary"
              sx={{
                minHeight: '40px',
                '& .MuiTabs-indicator': {
                  height: '1.5px',
                  borderTopLeftRadius: '3px',
                  borderTopRightRadius: '3px',
                  bgcolor: '#1d8ef0',
                },
              }}
            >
              <Tab
                label={`Feedback (${rules.length})`}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  color: '#1d8ef0',
                  minHeight: '40px',
                  py: 0,
                }}
              />
              <Tab
                label="Rule History"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.95rem',
                  color: '#64748b',
                  minHeight: '40px',
                  py: 0,
                }}
              />
            </Tabs>
          </Box>

          {/* Main Rules Card (Filters, Table) */}
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: 'white',
            }}
          >
            {/* Filters */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
              <Stack direction="row" spacing={2}>
                <TextField
                  size="small"
                  placeholder="Search rules..."
                  sx={{
                    width: 220,
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: '34px' },
                  }}
                />
                <Select
                  size="small"
                  value="all"
                  sx={{
                    width: 120,
                    bgcolor: 'white',
                    borderRadius: 1.5,
                    color: '#334155',
                    height: '34px',
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                </Select>
                <Select
                  size="small"
                  value="all"
                  sx={{
                    width: 120,
                    bgcolor: 'white',
                    borderRadius: 1.5,
                    color: '#334155',
                    height: '34px',
                  }}
                >
                  <MenuItem value="all">Status: All</MenuItem>
                </Select>
                <TextField
                  size="small"
                  placeholder="Select date range"
                  sx={{
                    width: 180,
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-root': { borderRadius: 1.5, height: '34px' },
                  }}
                />
              </Stack>
              <Button
                variant="contained"
                color="primary"
                disableElevation
                sx={{ height: '34px', textTransform: 'none', fontWeight: 600, borderRadius: 1.5 }}
              >
                + Add Rule from Feedback
              </Button>
            </Box>

            {/* Table */}
            <Box sx={{ borderTop: '1px solid #e2e8f0' }}>
              <GlobalMaterialTable
                isLoading={isLoading}
                columns={columns}
                data={rules}
                emptyMessage="No feedback found for this slot."
                enablePagination={false}
                enableBottomToolbar={false}
                enableTopToolbar={false}
                muiTablePaperProps={{
                  elevation: 0,
                  sx: {
                    border: 'none !important',
                    boxShadow: 'none !important',
                    borderRadius: 0,
                  },
                }}
                muiTableBodyRowProps={({ row }) => ({
                  onClick: () => handleRuleRowClick(row.original),
                  sx: {
                    cursor: 'pointer',
                    bgcolor: selectedRuleId === row.original.id ? '#f8fafc' : 'inherit',
                  },
                })}
                muiTableHeadCellProps={{
                  sx: {
                    bgcolor: 'white',
                    color: '#475569',
                    fontWeight: 600,
                    borderBottom: '1px solid #e2e8f0',
                  },
                }}
              />
            </Box>
          </Paper>

          {/* Effective Prompt Preview */}
          <Paper
            elevation={0}
            sx={{ border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', mb: 3 }}
          >
            {/* Effective Prompt Preview Header */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <VisibilityOutlined sx={{ fontSize: 20, color: '#1d8ef0' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Effective Prompt Preview
                </Typography>
                <Chip
                  label="Version -"
                  size="small"
                  sx={{
                    bgcolor: '#F6FEF9',
                    color: '#15803D',
                    border: '1px solid #DCFCE7',
                    fontWeight: 600,
                    fontSize: '0.72rem',
                    height: 22,
                    borderRadius: '12px',
                  }}
                />
              </Stack>
              <Button
                size="small"
                endIcon={<ContentCopyOutlined fontSize="small" />}
                sx={{ textTransform: 'none', color: '#334155', fontWeight: 600 }}
              >
                Copy Full Prompt
              </Button>
            </Box>

            {/* Three Columns */}
            <Grid container>
              {/* Base Prompt Column */}
              <Grid item xs={12} md={4} sx={{ p: 3, borderRight: '1px solid #e2e8f0' }}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ color: '#1e293b', fontWeight: 700 }}>
                      Base Prompt ({workflow || '-'})
                    </Typography>
                    <Link
                      component="button"
                      sx={{
                        textDecoration: 'none',
                        color: '#1d8ef0',
                        fontWeight: 600,
                        fontSize: '0.875rem',
                      }}
                    >
                      View
                    </Link>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ color: '#475569', lineHeight: 1.6, mb: 3, flexGrow: 1 }}
                  >
                    -
                  </Typography>
                </Box>
              </Grid>

              {/* Active Rules Column */}
              <Grid item xs={12} md={4} sx={{ p: 3, borderRight: '1px solid #e2e8f0' }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: '#1e293b',
                      fontWeight: 700,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: 1,
                        bgcolor: '#f0fdf4',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ListAltOutlined sx={{ fontSize: 14, color: '#16a34a' }} />
                    </Box>
                    Open Feedback ({openFeedbacks.length})
                  </Typography>
                  <Link
                    component="button"
                    sx={{
                      textDecoration: 'none',
                      color: '#1d8ef0',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  >
                    See all rules
                  </Link>
                </Box>
                <Stack spacing={2}>
                  {openFeedbacks.slice(0, 4).map((feedback, idx) => (
                    <Box key={feedback.id || idx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          bgcolor: '#dcfce3',
                          color: '#16a34a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          flexShrink: 0,
                          mt: 0.2,
                        }}
                      >
                        {idx + 1}
                      </Box>
                      <Typography variant="body2" sx={{ color: '#334155', lineHeight: 1.5 }}>
                        {feedback.comment || feedback.rule_text || '-'}
                      </Typography>
                    </Box>
                  ))}
                  {openFeedbacks.length === 0 && (
                    <Typography variant="body2" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                      No open feedback.
                    </Typography>
                  )}
                </Stack>
              </Grid>

              {/* Prompt Stats Column */}
              <Grid item xs={12} md={4} sx={{ p: 3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#1e293b',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: 1,
                      bgcolor: '#f0f9ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AssignmentOutlined sx={{ fontSize: 14, color: '#1d8ef0' }} />
                  </Box>
                  Prompt Stats
                </Typography>

                <Stack spacing={1.5} sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Open Feedback
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                      {openFeedbacks.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Resolved Feedback
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                      {resolvedFeedbacks.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Total Feedback
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                      {displayedFeedbacks.length}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ color: '#64748b' }}>
                      Last Feedback By
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 600 }}>
                      {lastFeedbackBy}
                    </Typography>
                  </Box>
                </Stack>

                <Typography
                  variant="caption"
                  sx={{ color: '#94a3b8', display: 'block', lineHeight: 1.4 }}
                >
                  Stats are based on feedback returned by the API.
                </Typography>
              </Grid>
            </Grid>

            {/* Info Banner at bottom */}
            <Box sx={{ p: 2, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <InfoOutlined sx={{ fontSize: 18, color: '#0369a1' }} />
                <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 500 }}>
                  Changes to rules (or parameters) are effective immediately in the live version of
                  the generated prompt.
                </Typography>
              </Stack>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default PromptRules;
